import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { LevelUpDTO, ProgressRewardsDTO } from '@anidex/shared/types';
import { AlertIcon, CheckIcon, CloseIcon, InfoIcon, SparkIcon } from './Icons';
import { LevelUpDialog } from './LevelUpDialog';

type Tone = 'info' | 'success' | 'reward' | 'error';

interface Toast {
  id: number;
  title: string;
  description?: string;
  tone: Tone;
}

interface ToastApi {
  push: (toast: Omit<Toast, 'id'>) => void;
  announceRewards: (rewards: ProgressRewardsDTO) => void;
}

const TONE_ICONS: Record<Tone, ComponentType<{ size?: number }>> = {
  info: InfoIcon,
  success: CheckIcon,
  reward: SparkIcon,
  error: AlertIcon,
};

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [levelUps, setLevelUps] = useState<LevelUpDTO[]>([]);
  const nextId = useRef(1);
  const region = useRef<HTMLDivElement>(null);

  // Modal dialogs live in the browser top layer; re-showing the popover puts toasts above them.
  useEffect(() => {
    const element = region.current;
    if (!element || typeof element.showPopover !== 'function') return;
    if (element.matches(':popover-open')) element.hidePopover();
    if (toasts.length > 0) element.showPopover();
  }, [toasts]);

  const dismiss = useCallback((id: number) => setToasts((current) => current.filter((toast) => toast.id !== id)), []);

  const push = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = nextId.current++;
      setToasts((current) => [...current.slice(-3), { ...toast, id }]);
      window.setTimeout(() => dismiss(id), toast.tone === 'error' ? 6500 : 4500);
    },
    [dismiss],
  );

  const announceRewards = useCallback(
    (rewards: ProgressRewardsDTO) => {
      if (rewards.levelUps.length > 0) setLevelUps((current) => [...current, ...rewards.levelUps]);
      for (const anime of rewards.completedAnimes) {
        push({ tone: 'success', title: t('toasts.animeCompleted'), description: anime.title });
      }
    },
    [push, t],
  );

  const value = useMemo(() => ({ push, announceRewards }), [push, announceRewards]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {levelUps.length > 0 && <LevelUpDialog levelUps={levelUps} onClose={() => setLevelUps([])} />}
      <div ref={region} popover="manual" className="toasts" role="status" aria-live="polite">
        {toasts.map((toast) => {
          const Icon = TONE_ICONS[toast.tone];
          return (
            <div key={toast.id} className={`toast toast--${toast.tone}`}>
              <span className="toast__icon" aria-hidden="true">
                <Icon size={22} />
              </span>
              <p className="toast__title">{toast.title}</p>
              {toast.description && <p className="toast__description">{toast.description}</p>}
              <button type="button" className="toast__close" onClick={() => dismiss(toast.id)} aria-label={t('toasts.close')}>
                <CloseIcon size={18} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToasts(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToasts must be used inside ToastProvider');
  return context;
}
